import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { fetchCompanyRoles, type RbacRole } from '../../../../services/rbacService'
import { useRbacStore } from '../../../../stores/rbacStore'
import { describeAccessError } from '../../../../utils/rbacMessages'
import { pickerGroups, pickerPermissions } from '../../../../utils/memberAccessDraft'

interface RolesPermissionsTabProps {
  companyId: string
}

/**
 * What each role allows, by permission group (SC-439). Read only: a company assigns roles and
 * single permissions to people (Users, Manage access) but doesn't edit the roles themselves.
 */
export const RolesPermissionsTab: React.FC<RolesPermissionsTabProps> = ({ companyId }) => {
  const catalogue = useRbacStore((s) => s.catalogue)
  const [roles, setRoles] = useState<RbacRole[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setRoles(null)
    setError(null)
    void useRbacStore.getState().ensureCatalogue()
    fetchCompanyRoles(companyId)
      .then((list) => {
        if (!cancelled) setRoles(list)
      })
      .catch((failure) => {
        if (!cancelled) setError(describeAccessError(failure, useRbacStore.getState().catalogue))
      })
    return () => {
      cancelled = true
    }
  }, [companyId])

  const permissions = useMemo(() => pickerPermissions(catalogue), [catalogue])
  const groups = useMemo(() => pickerGroups(catalogue), [catalogue])

  if (error) return <Alert severity="error">{error}</Alert>
  if (!roles) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading roles" />
      </Box>
    )
  }

  return (
    <Box data-testid="roles-permissions-tab">
      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
        Roles &amp; permissions
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        What each role allows. To change what one person can do, use Users, then Manage access.
        Roles themselves are set up by Sevv.
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ backgroundImage: 'none' }}>
        <Table size="small" aria-label="Roles and permissions">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Permission</TableCell>
              {roles.map((role) => (
                <TableCell
                  key={role.key}
                  align="center"
                  sx={{ fontWeight: 600 }}
                  data-testid={`role-column-${role.key}`}
                >
                  {role.name}
                  {!role.isSystem && (
                    <Chip size="small" variant="outlined" label="Custom" sx={{ ml: 0.75 }} />
                  )}
                  {role.description && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}
                    >
                      {role.description}
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              const rows = permissions.filter((p) => p.group === group)
              if (rows.length === 0) return null
              return (
                <React.Fragment key={group}>
                  <TableRow>
                    <TableCell
                      colSpan={roles.length + 1}
                      sx={{ bgcolor: 'action.hover', fontWeight: 600, color: 'text.primary' }}
                    >
                      {group}
                    </TableCell>
                  </TableRow>
                  {rows.map((permission) => (
                    <TableRow key={permission.key} data-testid={`role-row-${permission.key}`}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {permission.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {permission.description}
                        </Typography>
                      </TableCell>
                      {roles.map((role) => {
                        const included = role.permissions.includes(permission.key)
                        return (
                          <TableCell
                            key={role.key}
                            align="center"
                            data-included={included ? 'true' : 'false'}
                          >
                            {included ? (
                              <CheckIcon
                                fontSize="small"
                                color="success"
                                aria-label="Included"
                                role="img"
                              />
                            ) : (
                              <Typography
                                component="span"
                                sx={{ color: 'text.disabled' }}
                                aria-label="Not included"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default RolesPermissionsTab
